import {
  Flex, IconButton, Text
} from '@huspy/briks-web';
import { BankProduct } from '@modules/banks/api/types';
import { Grip, Trash2 } from '@huspy/briks-icons';
import briksTheme from '@shared/briks-theme';
import CurrencyFormatter from '@components/CurrencyFormatter';
import {
  arrayMove,
  SortableContext, sortableKeyboardCoordinates, useSortable
} from '@dnd-kit/sortable';
import {
  defaultDropAnimationSideEffects,
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { Active, DropAnimation } from '@dnd-kit/core';
import { useMemo, useState } from 'react';
import Loader from '@shared/loader';
import BankIcon from '@components/v2/BankIcon';
import Tooltip from '@components/Tooltip';
import { selectedBankProductsListStyles } from '@modules/proposals/presentation/v2/pages/ProposalCreation/features/SelectedBankProductsList/styles/styles.css';
import { useProposalCreationStore } from '@modules/proposals/presentation/v2/pages/ProposalCreation/store';

const {
  bankProductItem, bankProductIconPlaceholder, dragButton, loadingOverlay,
} = selectedBankProductsListStyles;

type BankProductItemProps = {
  product: BankProduct & { bank_icon?: string; icon?: string };
  onDelete?: (product: BankProduct) => Promise<void>;
  isOverlay?: boolean;
  isDisabled?: boolean;
};

const BankProductItem = ({
  product, onDelete, isOverlay, isDisabled,
}: BankProductItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: product.id, disabled: isDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isOverlay ? 0.5 : 1,
  };

  const handleOnDelete = () => {
    onDelete?.(product);
  };

  return (
    <Flex className={ bankProductItem } gap={ 2 } ref={ setNodeRef } style={ style } { ...attributes }>
      <Flex align='center'>
        <Grip color={ briksTheme.colors['neutral.400'] } className={ dragButton } { ...listeners } />
      </Flex>

      <Flex align='center'>
        <Tooltip label={ product.bank_name }>
          <Flex className={ bankProductIconPlaceholder }>
            <BankIcon
              icon={ product.icon ?? product.bank_icon }
              hasShadow={ false }
              style={ { height: '100%', width: '100%' } }
            />
          </Flex>
        </Tooltip>
      </Flex>

      <Flex direction='column' gap={ 1 } flex={ 1 }>
        <Text size='md' weight='semiBold'>
          <CurrencyFormatter currency='AED' value={ product.monthly_payment } />
          / monthly
        </Text>

        <Flex direction='column'>
          <Flex align='center' gap='1'>
            <Text color='text-secondary' size='md' weight='medium'>
              {product.interest_rate}
              %
              {' - '}
              {product.fixed_until}
              {' '}
              {product.fixed_until === 1
                ? 'year'
                : 'years'}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Flex align='center'>
        <IconButton
          size='sm'
          onClick={ handleOnDelete }
          variant='ghost'
        >
          <Trash2 />
        </IconButton>
      </Flex>
    </Flex>
  );
};

type BankProductsDndProps = {
  bankProducts: BankProduct[],
  activeId?: number,
  onDelete?: (product: BankProduct) => Promise<void>,
  isLoading?: boolean
};

const BankProductsDnd = (
  {
    bankProducts, activeId, onDelete, isLoading,
  }: BankProductsDndProps
) => (
  <Flex direction='column' gap={ 3 } position='relative'>
    {
      isLoading ? (
        <Flex
          align='center'
          justify='center'
          className={ loadingOverlay }
        >
          <Loader size={ 32 } />
        </Flex>
      ) : null
    }

    {
      bankProducts.map((bankProduct) => (
        <BankProductItem
          key={ bankProduct.id }
          product={ bankProduct }
          isOverlay={ activeId === bankProduct.id }
          isDisabled={ isLoading }
          onDelete={ onDelete }
        />
      ))
    }
  </Flex>
);

const dropAnimationConfig: DropAnimation = {
  sideEffects:
    defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.5' } } }),
};

export const SelectedBankProductsList = () => {
  const { bankProducts, setBankProducts } = useProposalCreationStore();

  const [activeItem, setActiveItem] = useState<Active | null>(null);

  const activeBankProduct = useMemo(
    () => bankProducts.find((item) => item.id === activeItem?.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeItem, bankProducts]
  );

  const onSave = async (products: BankProduct[]) => {
    setBankProducts(products);
  };

  const onDelete = async (product: BankProduct) => {
    const products = bankProducts.filter((item) => item.id !== product.id);

    setBankProducts(products);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <Flex gap={ 2 } direction='column' w='100%'>
      <Flex>
        <Text size='sm' color='text-secondary'>Your selected products appear here</Text>
      </Flex>

      <DndContext
        sensors={ sensors }
        onDragStart={ ({ active }) => {
          setActiveItem(active);
        } }
        onDragEnd={ ({ active, over }) => {
          if (over && active.id !== over?.id) {
            const activeIndex = bankProducts.findIndex(({ id }) => id === active.id);
            const overIndex = bankProducts.findIndex(({ id }) => id === over.id);

            const products = arrayMove(bankProducts, activeIndex, overIndex);

            onSave(products);
          }
          setActiveItem(null);
        } }
        onDragCancel={ () => {
          setActiveItem(null);
        } }
      >
        <SortableContext items={ bankProducts.map((item) => item.id) }>
          <BankProductsDnd
            bankProducts={ bankProducts }
            activeId={ activeBankProduct?.id }
            onDelete={ onDelete }
          />
        </SortableContext>
        <DragOverlay dropAnimation={ dropAnimationConfig }>
          {
            activeBankProduct ? (
              <BankProductItem
                key={ activeBankProduct.id }
                product={ activeBankProduct }
              />
            ) : null
          }
        </DragOverlay>
      </DndContext>
    </Flex>
  );
};
